import type { CeremonyType, ChecklistSection } from './types'

export interface ChecklistTemplateItem {
  /** Estable para siempre: identifica el item aunque cambie el titulo. */
  key: string
  section: ChecklistSection
  title: string
  detail?: string
  /**
   * Meses antes de la boda en que conviene tenerlo resuelto (0.25 = una semana
   * antes). Con la fecha de la boda puesta, se convierte en fecha limite
   * sugerida; la pareja puede cambiarla y su fecha manda sobre esta.
   */
  monthsBefore?: number
}

/** El nombre de la seccion cambia segun el tipo de ceremonia. */
export const SECTION_LABELS: Record<CeremonyType, Record<ChecklistSection, string>> = {
  cristiana: {
    espiritual: 'Espiritual y ceremonial',
    legal: 'Legal (Ecuador)',
    logistica: 'Logística general',
  },
  catolica: {
    espiritual: 'Sacramental y ceremonial',
    legal: 'Legal (Ecuador)',
    logistica: 'Logística general',
  },
  civil: {
    espiritual: 'Ceremonia',
    legal: 'Legal (Ecuador)',
    logistica: 'Logística general',
  },
}

/** Comun a los tres tipos de ceremonia: tramite civil en Ecuador. */
const LEGAL_ECUADOR: ChecklistTemplateItem[] = [
  {
    key: 'legal.regla_oro',
    section: 'legal',
    title: 'Entender la regla de oro: en Ecuador solo el matrimonio civil tiene valor legal',
    detail:
      'La ceremonia religiosa no produce efectos civiles y no existe convenio iglesia–Registro Civil (a diferencia de otros países). Hay que casarse ante el Registro Civil ANTES de la boda religiosa: la mayoría de iglesias exigen ver el acta civil antes de la bendición. Decidir ya la fecha del civil (recomendado: dos semanas antes o más).',
    monthsBefore: 4.5,
  },
  {
    key: 'legal.cedulas',
    section: 'legal',
    title: 'Verificar que las dos cédulas estén vigentes y en buen estado',
    detail:
      'Revisar fecha de caducidad de ambas cédulas. Si alguna está caducada o deteriorada, renovarla ya: turno en la Agencia Virtual (registrocivil.gob.ec), costo USD 16. El Registro Civil ya no exige la papeleta de votación como requisito, pero llevarla no estorba.',
    monthsBefore: 4.5,
  },
  {
    key: 'legal.sede',
    section: 'legal',
    title: 'Decidir dónde se celebra el civil: en agencia (USD 50) o fuera de sede (USD 250)',
    detail:
      'En agencia del Registro Civil cuesta USD 50; fuera de sede (casa, salón, jardín) cuesta USD 250, exige presentar un croquis del lugar y depende de disponibilidad de agenda, así que se pide con anticipación. Decidir si el civil será un acto íntimo previo (lo usual) o parte del evento principal.',
    monthsBefore: 4,
  },
  {
    key: 'legal.documentos',
    section: 'legal',
    title: 'Reunir documentos adicionales si alguno no es soltero ecuatoriano',
    detail:
      'Solo si aplica: divorciado, acta de matrimonio anterior con la marginación del divorcio inscrita; viudo, acta de defunción del cónyuge; extranjero, pasaporte o cédula, certificado de soltería/estado civil apostillado y traducido si corresponde. Si ambos son ecuatorianos solteros, no necesitan nada más que las cédulas.',
    monthsBefore: 4,
  },
  {
    key: 'legal.capitulaciones',
    section: 'legal',
    title: 'Decidir sobre capitulaciones matrimoniales ANTES de casarse',
    detail:
      'Por defecto el matrimonio crea sociedad conyugal (los bienes adquiridos son de los dos). Si quieren separación de bienes —relevante si hay negocio propio o deudas previas—, otorgar capitulaciones por escritura pública ante notario antes del matrimonio civil, o declararlo al momento de la celebración. Consultar costo notarial (referencial: USD 100-200 según cuantía).',
    monthsBefore: 3.5,
  },
  {
    key: 'legal.turno',
    section: 'legal',
    title: 'Agendar el matrimonio civil (o confirmar que no necesitan turno)',
    detail:
      'Si ambos son mayores de edad, solteros y sin hijos: NO necesitan turno, se acercan a cualquier agencia y el trámite es inmediato. Si alguno tiene hijos o estado civil anterior: turno obligatorio en la Agencia Virtual (registrocivil.gob.ec). Para fuera de sede, agendar sí o sí con semanas de anticipación y llevar el croquis.',
    monthsBefore: 3,
  },
  {
    key: 'legal.testigos',
    section: 'legal',
    title: 'Elegir y confirmar los dos testigos del civil',
    detail:
      'Requisitos: dos personas mayores de 18 años, idóneas, con cédula original vigente el día de la celebración. Pueden ser familiares; lo tradicional es que sean los padrinos de honor. Confirmar que puedan asistir en la fecha y hora exactas del civil y avisarles que lleven la cédula.',
    monthsBefore: 3,
  },
  {
    key: 'legal.registro_civil',
    section: 'legal',
    title: 'Celebrar el matrimonio civil y pedir copias del acta',
    detail:
      'Llevar ese día: cédulas originales de los novios y de los dos testigos y el comprobante de pago (USD 50 o 250). La ceremonia dura unos 20 minutos. Antes de salir, solicitar 2-3 copias certificadas del acta de matrimonio (aprox. USD 3 cada una): una es para la iglesia, otra para trámites.',
    monthsBefore: 0.5,
  },
  {
    key: 'legal.cedulas_nuevas',
    section: 'legal',
    title: 'Renovar las cédulas con el nuevo estado civil (después de la luna de miel)',
    detail:
      'El estado civil se actualiza en el sistema, pero conviene renovar la cédula física: turno en la Agencia Virtual, USD 16 por persona, llevar el acta de matrimonio. Sin apuro legal, pero hacerlo pronto evita inconsistencias en bancos y trámites.',
    monthsBefore: 0,
  },
  {
    key: 'legal.instituciones',
    section: 'legal',
    title: 'Actualizar IESS, bancos, seguros y beneficiarios',
    detail:
      'Tras el matrimonio: solicitar en el IESS la extensión de cobertura de salud al cónyuge si conviene (tiene un aporte adicional sobre el sueldo, verificar el porcentaje vigente); actualizar estado civil y beneficiarios en bancos, cooperativas, seguros de vida y fondos. Hacer la lista de instituciones ANTES de la boda para no olvidar ninguna.',
    monthsBefore: 0,
  },
]

const LOGISTICA_COMUN: ChecklistTemplateItem[] = [
  {
    key: 'logistica.salon',
    section: 'logistica',
    title: 'Cerrar el salón de recepción con contrato firmado',
    detail:
      'Diciembre es la temporada más disputada del año en Ecuador (Navidad, novenas, graduaciones, fiestas de empresas). Firmar ya: fecha exacta, horario y hora de cierre, aforo, qué incluye (mesas, sillas, mantelería, generador eléctrico, parqueo, guardianía), política de descorche, y penalidades de cancelación por escrito.',
    monthsBefore: 4.5,
  },
  {
    key: 'logistica.presupuesto',
    section: 'logistica',
    title: 'Fijar el presupuesto total y el calendario de pagos por proveedor',
    detail:
      'Una sola hoja (el panel de presupuesto del sitio): cada proveedor con anticipo pagado, saldo, fecha de pago y forma. Regla: nada se aparta sin contrato o recibo firmado, y todo pago queda registrado el mismo día.',
    monthsBefore: 4.5,
  },
  {
    key: 'logistica.invitados',
    section: 'logistica',
    title: 'Cerrar la lista de invitados con número final',
    detail:
      'El número manda sobre catering, salón e invitaciones: cerrarlo ya. Separar adultos y niños (el catering cobra distinto), definir política de acompañantes (¿los solteros llevan pareja?) y cargar todos al panel de invitados del sitio.',
    monthsBefore: 4.5,
  },
  {
    key: 'logistica.vestido',
    section: 'logistica',
    title: 'Vestido de novia: decidir compra o confección y fijar el calendario de pruebas',
    detail:
      'Decidir esta semana: la confección toma 2-3 meses. Calendario: encargo ya, segunda prueba a mediados del mes anterior, prueba final 7-10 días antes de la boda CON los zapatos y la ropa interior definitivos. Anotar quién lo transporta y dónde se guarda planchado.',
    monthsBefore: 4.5,
  },
  {
    key: 'logistica.catering',
    section: 'logistica',
    title: 'Contratar el catering y agendar la degustación',
    detail:
      'Degustación con fecha cerrada, probando el plato EXACTO que se servirá. Preguntar: precio por plato adulto y por menú infantil, opciones para diabéticos/vegetarianos/alergias, cuántos meseros por mesa, si incluye vajilla, cristalería y estaciones (café, postres), y hora precisa del servicio.',
    monthsBefore: 4,
  },
  {
    key: 'logistica.fotografia',
    section: 'logistica',
    title: 'Contratar fotografía y video viendo bodas completas',
    detail:
      'Pedir ver 2-3 bodas completas entregadas, no solo highlights. Cerrar en contrato: horas de cobertura (desde el arreglo hasta la lluvia de sobres), entregables (fotos editadas, video resumen, ceremonia completa), plazo de entrega, y quién es el fotógrafo que asiste (no «alguien del equipo»).',
    monthsBefore: 4,
  },
  {
    key: 'logistica.dj',
    section: 'logistica',
    title: 'Contratar DJ o grupo para la recepción',
    detail:
      'Definir el criterio musical de la pareja: qué géneros sí, lista explícita de «no tocar», y los momentos clave (entrada de novios, vals, agradecimientos, lluvia de sobres, hora loca si la habrá). Confirmar: equipo y luces propios, micrófono inalámbrico para brindis y pastor, costo de hora extra.',
    monthsBefore: 3.5,
  },
  {
    key: 'logistica.decoracion',
    section: 'logistica',
    title: 'Contratar decoración y flores con mock-up previo',
    detail:
      'Ecuador es potencia mundial en rosas: aprovechar flor nacional de exportación a precio local. Definir: arco o altar, centros de mesa, pasillo de la iglesia, y pedir un mock-up (mesa de muestra armada) antes de firmar. Confirmar hora de montaje con el salón.',
    monthsBefore: 3.5,
  },
  {
    key: 'logistica.luna_miel',
    section: 'logistica',
    title: 'Reservar la luna de miel y verificar documentos de viaje',
    detail:
      'Reservar vuelos y hotel YA (diciembre es temporada alta). Clave: reservar con el nombre TAL CUAL aparece en el pasaporte (el apellido no cambia con el matrimonio). Verificar: pasaportes con 6+ meses de vigencia, visas si el destino lo exige, seguro de viaje, y avisar al banco del viaje.',
    monthsBefore: 3.5,
  },
  {
    key: 'logistica.anillos',
    section: 'logistica',
    title: 'Comprar los anillos y mandarlos a grabar',
    detail:
      'Medirse las tallas en joyería (no adivinar). Encargar el grabado —nombres y fecha, o la cita del cordón (Ecl. 4:12)— contando 2-4 semanas de plazo. Decidir quién los custodia hasta la ceremonia (padrino de anillos) y comprar un porta-anillos digno de las fotos.',
    monthsBefore: 3,
  },
  {
    key: 'logistica.traje',
    section: 'logistica',
    title: 'Traje del novio: comprar o confeccionar',
    detail:
      'Definir con la paleta de la boda. Prueba final 2 semanas antes. Detalles que se olvidan: zapatos ablandados con uso previo (no estrenar el día), correa, medias, camisa de repuesto, y quién plancha y transporta el traje.',
    monthsBefore: 3,
  },
  {
    key: 'logistica.invitaciones',
    section: 'logistica',
    title: 'Enviar las invitaciones y abrir las confirmaciones',
    detail:
      'Enviar por WhatsApp con el enlace del sitio y el deep-link de confirmación de cada familia. Fecha límite de RSVP: un mes antes de la boda. Programar recordatorio a los que no respondan a las 2 semanas.',
    monthsBefore: 3,
  },
  {
    key: 'logistica.hospedaje',
    section: 'logistica',
    title: 'Bloquear hospedaje para invitados de fuera',
    detail:
      'Negociar tarifa de grupo en 1-2 hoteles cerca del salón, publicar nombres, precios y fecha límite de reserva en el sitio de la boda. Diciembre se llena: el bloqueo debe quedar firmado este mes. Anotar quién de la familia recibe y orienta a los que llegan.',
    monthsBefore: 3,
  },
  {
    key: 'logistica.pastel',
    section: 'logistica',
    title: 'Pastel: degustación, contrato y logística de entrega',
    detail:
      'Degustar 2-3 sabores con la pastelera. Definir: pisos según número de invitados (y si habrá base falsa), topper, quién lo transporta y lo monta en el salón, a qué hora se entrega y dónde se refrigera. Dejar por escrito la hora del corte en el cronograma.',
    monthsBefore: 3,
  },
  {
    key: 'logistica.maquillaje',
    section: 'logistica',
    title: 'Maquillaje y peinado: contratar con prueba pagada',
    detail:
      'Hacer la prueba con fotos en luz natural y con el tono del vestido. Definir la hora de inicio del día retrocediendo desde la ceremonia (maquillaje + peinado + vestirse + fotos + traslado). Preguntar si atiende también a mamás y damas, y pedir kit de retoque para la noche.',
    monthsBefore: 2.5,
  },
  {
    key: 'logistica.recuerdos',
    section: 'logistica',
    title: 'Encargar los recuerdos para invitados',
    detail:
      'Definir el recuerdo (una tarjeta con un versículo + dulce típico funciona y es coherente con la boda). Calcular cantidad por FAMILIAS, no por personas, más 10%. Asignar quién los coloca en las mesas el día del montaje.',
    monthsBefore: 2,
  },
  {
    key: 'logistica.transporte',
    section: 'logistica',
    title: 'Resolver el transporte del día',
    detail:
      'Auto de los novios con chofer designado (nombre y teléfono en el cronograma), decoración del auto, traslado iglesia→salón para padres y abuelos, y si hay invitados sin auto: cotizar una buseta con hora de salida publicada. Calcular tiempos reales de tráfico de un sábado de diciembre.',
    monthsBefore: 2,
  },
  {
    key: 'logistica.regalos',
    section: 'logistica',
    title: 'Definir mesa de regalos o cuenta para transferencias',
    detail:
      'Decidir si además de la lluvia de sobres habrá lista de regalos o datos de cuenta bancaria publicados en el sitio. Si es cuenta: publicar banco, tipo, número y titular exactos. Evitar duplicar canales que confundan al invitado.',
    monthsBefore: 2,
  },
  {
    key: 'logistica.plan_lluvia',
    section: 'logistica',
    title: 'Asegurar el plan B de lluvia',
    detail:
      'Diciembre abre la temporada lluviosa en la Sierra. Confirmar por escrito con el salón: carpa o área techada con la misma capacidad, ruta techada para la entrada de los novios, y paraguas blancos para las fotos de exteriores. Decidir de antemano QUIÉN activa el plan B y a qué hora se decide.',
    monthsBefore: 2,
  },
  {
    key: 'logistica.coordinador',
    section: 'logistica',
    title: 'Nombrar coordinador del día y armar la coreografía del primer baile',
    detail:
      'Elegir a una persona de confianza (no los novios, no las mamás) como coordinador del día: recibe proveedores, resuelve imprevistos y tiene todos los teléfonos. Si habrá vals o baile de entrada, ensayarlo desde ahora, con la canción definitiva y los zapatos de la boda.',
    monthsBefore: 1.5,
  },
  {
    key: 'logistica.lluvia_sobres',
    section: 'logistica',
    title: 'Organizar la lluvia de sobres de punta a punta',
    detail:
      'Protocolo exacto: el DJ la anuncia tras el vals con la canción elegida; buzón o cofre con ranura en la mesa principal; UN responsable de total confianza (padrino o hermano) lo vigila toda la noche, recoge los sobres al terminar y se los lleva ESA MISMA noche; al día siguiente dos personas cuentan juntas, anotan quién dio cuánto (para agradecer) y depositan el lunes.',
    monthsBefore: 1,
  },
  {
    key: 'logistica.numero_final',
    section: 'logistica',
    title: 'Cerrar confirmaciones y entregar el número final',
    detail:
      'Llamar uno a uno a los que no respondieron (no basta el mensaje). Entregar el número final y el detalle de menús infantiles y dietas al catering y al salón: la mayoría lo exige 7-10 días antes y cobra por lo confirmado.',
    monthsBefore: 1,
  },
  {
    key: 'logistica.asientos',
    section: 'logistica',
    title: 'Armar la distribución de mesas',
    detail:
      'Croquis del salón con nombres por mesa: mesa de honor, mesas de las dos familias, mesa del pastor y líderes de la iglesia, mesa de jóvenes, niños cerca de sus padres. Imprimir el plano para la entrada y tarjetas o letreros por mesa.',
    monthsBefore: 0.5,
  },
  {
    key: 'logistica.cronograma_dia',
    section: 'logistica',
    title: 'Redactar el cronograma del día minuto a minuto y repartirlo',
    detail:
      'Desde el peinado hasta el cierre del salón, con hora, lugar y responsable de cada hito (llegadas de proveedores incluidas). Enviarlo a TODOS: proveedores, padrinos, coordinador del día y familia directa. Publicar la versión pública en el programa del sitio.',
    monthsBefore: 0.5,
  },
  {
    key: 'logistica.ronda_final',
    section: 'logistica',
    title: 'Hacer la ronda final de confirmación con todos los proveedores',
    detail:
      'La semana de la boda, llamada corta a cada proveedor: hora de llegada, persona que asiste, teléfono del día, saldo pendiente y forma de pago (preparar sobres etiquetados con los saldos en efectivo). Pedir prueba de sonido y luces en el salón si es posible.',
    monthsBefore: 0.25,
  },
  {
    key: 'logistica.kit_emergencia',
    section: 'logistica',
    title: 'Armar el kit de emergencia y las maletas',
    detail:
      'Kit: costurero, imperdibles, quitamancha instantáneo, cinta para bastas, analgésicos, antiácido, curitas, desodorante, hilo dental, cargadores, copia impresa del cronograma, efectivo suelto y snacks para los novios (nadie come el día de su boda). Aparte: maleta de la noche de bodas y de la luna de miel listas ANTES del gran día.',
    monthsBefore: 0.25,
  },
]

/** Ceremonia cristiana evangelica: la plantilla del piloto, investigada a fondo. */
const CRISTIANA: ChecklistTemplateItem[] = [
  {
    key: 'espiritual.pastor',
    section: 'espiritual',
    title: 'Reunir a la pareja con el pastor para confirmar fecha, hora y requisitos de la iglesia',
    detail:
      'Pedir formalmente la bendición y confirmar por escrito: fecha, hora de la ceremonia, disponibilidad del templo (diciembre está lleno de programas navideños), y qué exige la iglesia antes de casar: consejería obligatoria, membresía o bautismo, y si piden el acta del matrimonio civil antes de la ceremonia religiosa (la mayoría de iglesias evangélicas en Ecuador sí la piden).',
    monthsBefore: 4.5,
  },
  {
    key: 'espiritual.consejeria',
    section: 'espiritual',
    title: 'Inscribirse en la consejería prematrimonial: 6 a 8 sesiones',
    detail:
      'Agendar con el pastor o el matrimonio consejero sesiones quincenales que terminen un mes antes de la boda. Temas que deben cubrirse (pedir el temario y verificar que estén): comunicación y resolución de conflictos, finanzas y presupuesto del hogar, intimidad y sexualidad, roles y expectativas, relación con las familias de origen y límites, y el liderazgo espiritual del nuevo hogar.',
    monthsBefore: 4.5,
  },
  {
    key: 'espiritual.devocional',
    section: 'espiritual',
    title: 'Establecer la vida devocional como pareja desde ya',
    detail:
      'Fijar un día y hora semanal para leer la Biblia y orar juntos por la boda y por el matrimonio (no solo por la logística). Elegir un plan o devocional para novios y sostenerlo hasta la boda: es el hábito que se llevan al matrimonio.',
    monthsBefore: 4.5,
  },
  {
    key: 'espiritual.padrinos',
    section: 'espiritual',
    title: 'Elegir a los padrinos y asignar a cada uno su rol',
    detail:
      'Definir y confirmar disponibilidad: padrinos de honor (acompañan y son testigos espirituales, suelen ser también los testigos del civil), padrinos de arras (las entregan), padrinos de anillos (los guardan y presentan) y quien presenta el cordón de tres dobleces. Explicarles a cada uno qué hace, cuándo y dónde se ubica; invitarlos al ensayo.',
    monthsBefore: 4,
  },
  {
    key: 'espiritual.estructura',
    section: 'espiritual',
    title: 'Definir con el pastor la estructura completa de la ceremonia',
    detail:
      'Acordar el orden y la duración estimada: procesional (quién entra y en qué orden), alabanza congregacional, mensaje, votos, anillos, arras, cordón de tres dobleces, declaración de matrimonio, firma del pacto, oración de bendición sobre la pareja y recesional. Dejarlo escrito: ese documento alimenta el programa impreso y el ensayo.',
    monthsBefore: 3,
  },
  {
    key: 'espiritual.musica',
    section: 'espiritual',
    title: 'Definir la música de la ceremonia: quién toca y qué se canta',
    detail:
      'Decidir si toca el grupo de alabanza de la iglesia o músicos invitados. Elegir: canción de entrada de la novia, 2-3 alabanzas congregacionales, música para el momento del cordón y la firma, y canción de salida. Confirmar equipo de sonido del templo y agendar un ensayo con los músicos.',
    monthsBefore: 3,
  },
  {
    key: 'espiritual.cordon',
    section: 'espiritual',
    title: 'Encargar el cordón de tres dobleces',
    detail:
      'Mandar a hacer o comprar el cordón (Eclesiastés 4:12): tres hebras que representan a Dios, al esposo y a la esposa. Decidir colores (que dialoguen con la paleta de la boda), quién lo trenza durante la ceremonia (usualmente los novios mientras el pastor lee el texto), en qué base o atril se exhibe y quién lo lleva ese día.',
    monthsBefore: 2.5,
  },
  {
    key: 'espiritual.arras',
    section: 'espiritual',
    title: 'Comprar las arras y acordar su momento en la ceremonia',
    detail:
      'Comprar el cofre con las 13 monedas (hay en joyerías y tiendas cristianas). Acordar con el pastor las palabras del intercambio (provisión y administración fiel de los bienes del hogar) y confirmar que los padrinos de arras sepan cuándo entregarlas.',
    monthsBefore: 2.5,
  },
  {
    key: 'espiritual.votos',
    section: 'espiritual',
    title: 'Escribir los votos y elegir los textos bíblicos con sus lectores',
    detail:
      'Decidir: votos tradicionales, votos propios o ambos. Elegir 2-3 textos (Génesis 2:24, 1 Corintios 13, Eclesiastés 4:9-12 son los clásicos del cordón) y asignar cada lectura a una persona concreta. Si escriben votos propios, tenerlos listos un mes antes y llevar copia impresa el día (nadie memoriza bajo nervios).',
    monthsBefore: 2,
  },
  {
    key: 'espiritual.testimonio',
    section: 'espiritual',
    title: 'Compartir el testimonio de la pareja con el pastor para el mensaje',
    detail:
      'Reunirse solo para esto: contarle cómo se conocieron, cómo Dios los guio y qué quieren que la ceremonia comunique a los invitados no creyentes. Acordar el enfoque y la duración del mensaje (15-20 minutos funciona; más, con invitados de pie o con niños, no).',
    monthsBefore: 2,
  },
  {
    key: 'espiritual.participaciones',
    section: 'espiritual',
    title: 'Definir las participaciones especiales de familia e iglesia',
    detail:
      'Cerrar la lista: quién entrega a la novia, quién hace la oración por la pareja, si los padres dan palabras o una bendición, y si hay participación especial (canto, poesía). Avisar a cada persona con al menos un mes para que prepare.',
    monthsBefore: 1.5,
  },
  {
    key: 'espiritual.pacto',
    section: 'espiritual',
    title: 'Preparar el documento del pacto matrimonial y su firma',
    detail:
      'Mandar a imprimir o caligrafiar el certificado de pacto que firmarán los novios, el pastor y los padrinos durante la ceremonia (es simbólico, no legal). Conseguir una pluma digna de fotos y decidir dónde se enmarcará en casa.',
    monthsBefore: 1,
  },
  {
    key: 'espiritual.programa',
    section: 'espiritual',
    title: 'Diseñar e imprimir el programa de la ceremonia',
    detail:
      'Una hoja con el orden del culto, los nombres de quienes participan, las letras de las alabanzas congregacionales (los invitados no creyentes no las conocen) y el versículo del cordón. Imprimir 10% más que confirmados.',
    monthsBefore: 1,
  },
  {
    key: 'espiritual.ensayo',
    section: 'espiritual',
    title: 'Realizar el ensayo general de la ceremonia',
    detail:
      'Convocar en el templo a pastor, cortejo, padres, padrinos y músicos. Ensayar: entradas con la música real, ubicaciones, el trenzado del cordón, la entrega de arras y anillos, y la salida. Llevar el cordón real y anillos de práctica. Máximo una hora; cerrar orando juntos.',
    monthsBefore: 0.25,
  },
  ...LEGAL_ECUADOR,
  ...LOGISTICA_COMUN,
]

const CATOLICA: ChecklistTemplateItem[] = [
  {
    key: 'espiritual.curso_prematrimonial',
    section: 'espiritual',
    title: 'Curso prematrimonial y pláticas en la parroquia',
    detail: 'Reservar con varios meses de anticipación; algunas parroquias piden hasta 6 meses.',
  },
  {
    key: 'espiritual.expediente',
    section: 'espiritual',
    title: 'Abrir el expediente matrimonial en la parroquia',
    detail: 'Fe de bautismo y de confirmación actualizadas de ambos, más partidas y testigos.',
  },
  {
    key: 'espiritual.reserva_parroquia',
    section: 'espiritual',
    title: 'Reservar la iglesia y confirmar al sacerdote celebrante',
  },
  {
    key: 'espiritual.lecturas',
    section: 'espiritual',
    title: 'Elegir las lecturas y el evangelio de la misa nupcial',
  },
  {
    key: 'espiritual.ritos',
    section: 'espiritual',
    title: 'Definir los ritos: anillos, arras, lazo y ofrenda a la Virgen',
  },
  {
    key: 'espiritual.musica',
    section: 'espiritual',
    title: 'Coordinar la música litúrgica con el coro de la parroquia',
  },
  {
    key: 'espiritual.padrinos',
    section: 'espiritual',
    title: 'Definir padrinos de velación, anillos, arras y lazo',
  },
  {
    key: 'espiritual.ensayo',
    section: 'espiritual',
    title: 'Ensayo general — fecha y lugar',
  },
  ...LEGAL_ECUADOR,
  ...LOGISTICA_COMUN,
]

const CIVIL: ChecklistTemplateItem[] = [
  {
    key: 'espiritual.formato_ceremonia',
    section: 'espiritual',
    title: 'Definir el formato de la ceremonia',
    detail: 'Entrada, palabras de bienvenida, votos, intercambio de anillos, firma y brindis.',
  },
  {
    key: 'espiritual.oficiante',
    section: 'espiritual',
    title: 'Confirmar oficiante o maestro de ceremonias',
  },
  {
    key: 'espiritual.votos',
    section: 'espiritual',
    title: 'Escribir los votos',
  },
  {
    key: 'espiritual.musica',
    section: 'espiritual',
    title: 'Selección de música: entrada, firma y salida',
  },
  {
    key: 'espiritual.ensayo',
    section: 'espiritual',
    title: 'Ensayo general — fecha y lugar',
  },
  ...LEGAL_ECUADOR,
  ...LOGISTICA_COMUN,
]

export const CHECKLIST_TEMPLATES: Record<CeremonyType, readonly ChecklistTemplateItem[]> = {
  cristiana: CRISTIANA,
  catolica: CATOLICA,
  civil: CIVIL,
}

export function templateFor(ceremonyType: CeremonyType): readonly ChecklistTemplateItem[] {
  return CHECKLIST_TEMPLATES[ceremonyType] ?? CHECKLIST_TEMPLATES.cristiana
}
